import React from "react";
import {useCsContext} from "../../state/CsInstance";
import {observer} from "mobx-react-lite";

import ModalUploadContent from "../Modals/ModalUploadContent";
import ModalDownloadContent from "../Modals/ModalDownloadContent";

import {faBook} from "@fortawesome/sharp-solid-svg-icons/faBook";
import {faImages} from "@fortawesome/sharp-solid-svg-icons/faImages";
import {faSidebar} from "@fortawesome/sharp-solid-svg-icons/faSidebar";
import {faReadme} from "@fortawesome/free-brands-svg-icons/faReadme";
import {faArrowProgress} from "@fortawesome/sharp-solid-svg-icons/faArrowProgress";
import {faFileArrowDown} from "@fortawesome/sharp-solid-svg-icons/faFileArrowDown";
import {faGear} from "@fortawesome/sharp-solid-svg-icons/faGear";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";

import createSidebarMethods from "../../util/sidebarMethods";
import {TheaterSize} from "../../state/theaterStore";

import styles from "./Sidebar.module.scss";
import {convertMarkdownToHTML} from "../../util/htmlTranslator";

type SidebarProps = {};
function Sidebar(props: SidebarProps) {
	const csInstance = useCsContext();
	const {featureFlagStore, codeStore, theaterStore, globalModalStore} =
		csInstance;

	const sidebarMethods = createSidebarMethods();

	// Helper for upload modal
	function readFileContent(file: File) {
		const reader = new FileReader();
		return new Promise<string>((resolve, reject) => {
			reader.onload = (event) => {
				const content = event.target?.result;
				if (content && typeof content === "string") {
					resolve(content);
				} else {
					reject("File content is not a string");
				}
			};
			reader.onerror = (error) => reject(error);
			reader.readAsText(file);
		});
	}

	const UploadModalContent = (
		<ModalUploadContent
			title="Upload new code"
			text="Currently only for frontend testing purposes."
			onSuccess={(file?: File) => {
				if (file) {
					readFileContent(file)
						.then((content) => {
							if (content) {
								theaterStore.isRunning = false;
								csInstance.intakeProjectYaml(content);
							}
						})
						.catch((error) => console.log(error));
				}
			}}
		/>
	);

	const DownloadModalContent = (
		<ModalDownloadContent
			title="Download Project"
			onSuccess={(projectName: string) => {
				const outputYaml = codeStore.outputActivityYaml(projectName);
				const element = document.createElement("a");
				const file = new Blob([outputYaml], {type: "text/plain"});
				element.href = URL.createObjectURL(file);
				if (!projectName) {
					projectName = codeStore.projectName || "project";
				}
				element.download = `${projectName}.yaml`;
				document.body.appendChild(element);
				element.click();
				element.remove();
			}}
		/>
	);

	return (
		<div className={styles.sidebarContainer}>
			<div className={styles.sidebarPrimaryButtonsContainer}>
				<img
					crossOrigin="anonymous"
					src="https://cs-curriculum-assets.artofproblemsolving.com/PixiSprites/aops.png"
					alt="AopsTheater Logo"
					className={styles.logo}
				/>
				<div className={styles.divider} />
				<SidebarButton
					title={"Side Panel"}
					icon={
						<FontAwesomeIcon icon={faSidebar} className={styles.sidebarIcon} />
					}
					onClick={() => sidebarMethods.toggleTheater.onClick({csInstance})}
					isActive={theaterStore.theaterSize !== TheaterSize.None}
				/>
				<SidebarButton
					title={"Documentation"}
					icon={
						<FontAwesomeIcon icon={faBook} className={styles.sidebarIcon} />
					}
					onClick={() => sidebarMethods.documentation.onClick({csInstance})}
				/>
				<SidebarButton
					title={"Image Assets"}
					icon={
						<FontAwesomeIcon icon={faImages} className={styles.sidebarIcon} />
					}
					onClick={() => sidebarMethods.imageAssets.onClick({csInstance})}
				/>
				{featureFlagStore.showExtraButtons && (
					<>
						<div className={styles.divider} />

						<div className={styles.helperButtonsContainer} />
						<SidebarButton
							title="README"
							icon={
								<FontAwesomeIcon
									icon={faReadme}
									className={styles.sidebarIcon}
								/>
							}
							variant="clear"
							isActive={codeStore.isMarkdownEditMode}
							onClick={() =>
								(codeStore.isMarkdownEditMode = !codeStore.isMarkdownEditMode)
							}
						/>
						<SidebarButton
							title="Convert Markdown to HTML"
							icon={
								<FontAwesomeIcon
									icon={faArrowProgress}
									className={styles.sidebarIcon}
								/>
							}
							variant="clear"
							// If Instructions-raw.md and Instructions.md both exist, convert
							// the raw markdown to html and replace Instructions.md with it
							onClick={() => {
								const fileMap = codeStore.fileMap;
								const instructionsRawContents =
									fileMap?.["Instructions-raw.md"]?.value;
								const instructionsFileDoesExist =
									!!fileMap?.["Instructions.md"];
								if (instructionsRawContents && instructionsFileDoesExist) {
									const instructionsHtml = convertMarkdownToHTML(
										instructionsRawContents
									);

									codeStore.hasUncompiledChanges = true;
									codeStore.setFileData("Instructions.md", {
										name: "Instructions.md",
										value: instructionsHtml,
										attributes:
											codeStore.fileMap["Instructions.md"]?.attributes,
									});
									codeStore.saveProjectToLocalStorage();
								}
							}}
						/>
						<SidebarButton
							title="Download"
							icon={
								<FontAwesomeIcon
									icon={faFileArrowDown}
									className={styles.sidebarIcon}
								/>
							}
							variant="clear"
							onClick={() => globalModalStore.showModal(DownloadModalContent)}
						/>
					</>
				)}
			</div>
			<div className={styles.sidebarPrimaryButtonsContainer}>
				<SidebarButton
					title="Settings"
					icon={
						<FontAwesomeIcon icon={faGear} className={styles.sidebarIcon} />
					}
					isDisabled={!featureFlagStore.showExtraButtons}
					onClick={() => globalModalStore.showModal(UploadModalContent)}
				/>
			</div>
		</div>
	);
}

type SidebarButtonProps = {
	title: string;
	icon: JSX.Element;
	variant?: "default" | "clear";
	isActive?: boolean;
	isDisabled?: boolean;
	onClick?: () => void;
};

function SidebarButton(props: SidebarButtonProps) {
	const {
		icon,
		title,
		variant = "default",
		isActive = false,
		isDisabled = false,
		onClick,
	} = props;

	const classNames = [styles.sidebarButton];
	if (variant === "clear") {
		classNames.push(styles.sidebarButtonClear);
	}
	if (isActive) {
		classNames.push(styles.sidebarButtonActive);
	}

	return (
		<button
			className={classNames.join(" ")}
			onClick={onClick}
			disabled={isDisabled}
			title={title}
		>
			{icon}
		</button>
	);
}

export default observer(Sidebar);
